"use client";

import { type ReactNode, useId } from "react";
import {
  Checkbox,
  CopyButton,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import type {
  SourceCitationStrings,
  SourceField,
  SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  LABEL,
  NOTICE,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { escapeHtml } from "@/lib/tool-kit/citation-parts";
import {
  type RichPart,
  richToPlain,
} from "@/lib/tool-kit/special-sources/common";

/**
 * The special-sources generators' form-and-results shell, for the three that the shared
 * `SourceCitationBuilder` cannot serve as it stands.
 *
 * It draws the same form and the same result cards, but the values live in the
 * widget rather than inside the shell. Two generators need that: the clinical-trial
 * and GitHub generators fill the form from a lookup, and a form that owns its own
 * state cannot be filled from outside. The figure generator needs one more thing the
 * shared cards cannot draw — APA's bold figure number — and the GitHub generator a
 * BibTeX record shown as code. If the shared builder grows a controlled mode, bold
 * parts and code entries, these widgets can move onto it and this file can go.
 */

/** One entry: a citation (parts may be bold or italic), or a block of code. */
export interface FormEntry {
  label: string;
  parts: RichPart[];
  /** Monospace with line breaks kept — a BibTeX record. */
  code?: boolean;
}

export interface FormOutput {
  key: string;
  label: string;
  entries: FormEntry[];
  note?: string;
}

/** HTML for the rich copy: `<b>` and `<i>` survive the paste into Word. */
export function richToHtml(parts: readonly RichPart[]): string {
  return parts
    .map((part) => {
      let html = escapeHtml(part.text);
      if (part.italic) {
        html = `<i>${html}</i>`;
      }
      if (part.bold) {
        html = `<b>${html}</b>`;
      }
      return html;
    })
    .join("");
}

function FieldControl({
  field,
  id,
  value,
  onChange,
}: {
  field: SourceField;
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  if (field.kind === "checkbox") {
    return (
      <div className="space-y-1">
        <Checkbox
          checked={value === "true"}
          label={field.label}
          onChange={(checked) => onChange(checked ? "true" : "")}
        />
        {field.hint ? <p className={HINT}>{field.hint}</p> : null}
      </div>
    );
  }
  if (field.kind === "select") {
    return (
      <SelectField
        hint={field.hint}
        id={id}
        label={field.label}
        onChange={onChange}
        options={field.options ?? []}
        value={value}
      />
    );
  }
  if (field.kind === "textarea" || field.kind === "people") {
    return (
      <TextAreaField
        hint={field.hint}
        id={id}
        label={field.label}
        onChange={onChange}
        placeholder={field.placeholder}
        prose
        rows={field.kind === "people" ? 3 : 4}
        value={value}
      />
    );
  }
  return (
    <TextField
      hint={field.hint}
      id={id}
      label={field.label}
      onChange={onChange}
      placeholder={field.placeholder}
      type={field.kind === "url" ? "url" : "text"}
      value={value}
    />
  );
}

function Rich({ parts }: { parts: readonly RichPart[] }) {
  return (
    <>
      {parts.map((part, index) => {
        let node: ReactNode = part.text;
        if (part.italic) {
          node = <i>{node}</i>;
        }
        if (part.bold) {
          node = <b>{node}</b>;
        }
        // Parts are positional fragments of one sentence.
        // biome-ignore lint/suspicious/noArrayIndexKey: positional fragments
        return <span key={index}>{node}</span>;
      })}
    </>
  );
}

function EntryBody({ entry }: { entry: FormEntry }) {
  if (entry.code) {
    return (
      <pre className="min-w-0 flex-1 overflow-x-auto whitespace-pre rounded-md bg-muted/40 p-3 font-mono text-foreground text-xs leading-relaxed">
        {richToPlain(entry.parts)}
      </pre>
    );
  }
  return (
    <p className="break-words pl-6 -indent-6 text-foreground leading-relaxed">
      <Rich parts={entry.parts} />
    </p>
  );
}

function OutputCard({
  output,
  strings,
}: {
  output: FormOutput;
  strings: SourceCitationStrings;
}) {
  return (
    <section className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{output.label}</h3>
      {output.entries.map((entry) => (
        <div className="space-y-2" key={entry.label}>
          <p className={LABEL}>{entry.label}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <EntryBody entry={entry} />
            <div className="shrink-0">
              <CopyButton
                copiedLabel={strings.copied}
                html={entry.code ? undefined : richToHtml(entry.parts)}
                label={strings.copy}
                small
                text={richToPlain(entry.parts)}
              />
            </div>
          </div>
        </div>
      ))}
      {output.note ? (
        <p className="text-muted-foreground text-sm">{output.note}</p>
      ) : null}
    </section>
  );
}

export function CitationForm({
  fields,
  values,
  onChange,
  onExample,
  onClear,
  outputs,
  strings,
  intro,
}: {
  fields: readonly SourceField[];
  values: SourceValues;
  onChange: (id: string, value: string) => void;
  onExample: () => void;
  onClear: () => void;
  /** Already formatted — empty until enough is entered. */
  outputs: readonly FormOutput[];
  strings: SourceCitationStrings;
  /** Content between the heading and the fields: a lookup, a mode switch. */
  intro?: ReactNode;
}) {
  const prefix = useId();
  const empty = Object.values(values).every((value) => value === "");

  return (
    <>
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.formHeading}</h2>
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SECONDARY}
              onClick={onExample}
              type="button"
            >
              {strings.tryExample}
            </button>
            <button
              className={BUTTON_SECONDARY}
              disabled={empty}
              onClick={onClear}
              type="button"
            >
              {strings.clear}
            </button>
          </div>
        </div>
        {intro}
        <div className="grid gap-4 sm:grid-cols-2">
          {fields.map((field) => (
            <div
              className={
                field.wide || field.kind === "textarea" ? "sm:col-span-2" : ""
              }
              key={field.id}
            >
              <FieldControl
                field={field}
                id={`${prefix}-${field.id}`}
                onChange={(value) => onChange(field.id, value)}
                value={values[field.id] ?? ""}
              />
            </div>
          ))}
        </div>
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {outputs.length === 0 ? (
          <p className={NOTICE}>{strings.emptyPrompt}</p>
        ) : (
          outputs.map((output) => (
            <OutputCard key={output.key} output={output} strings={strings} />
          ))
        )}
      </section>
    </>
  );
}
